import React, { useState, useEffect } from 'react';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';
import { FileMetadata } from '@/models/Post';
import { fileService } from '../../services/fileService';
import PrivacyConsent from '../forms/PrivacyConsent';
import { facebookPixelEvents } from '../../utils/facebookPixel';

const portalId = import.meta.env.VITE_HUBSPOT_PORTAL_ID;
const formId = import.meta.env.VITE_HUBSPOT_DOWNLOAD_FORM_ID;

interface SignUpFormProps {
  onSuccess?: () => void;
  downloadFile: FileMetadata;
}

const schoolTypes = [
  'Individual household',
  'HomeSchool Coop',
  'MicroSchools',
  'Other'
  
] as const;

const studentAgeRanges = [
  'Middle School',
  'High School',
  'Other'
  
] as const;

export default function SignUpForm({ onSuccess, downloadFile }: SignUpFormProps) {
  console.log('SignUpForm received raw downloadFile:', downloadFile);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    type_of_homeschooling: '',
    student_age: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Reset status after 3 seconds if there's an error
  useEffect(() => {
    if (submitStatus === 'error') {
      const timer = setTimeout(() => setSubmitStatus('idle'), 3000);
      return () => clearTimeout(timer);
    }
  }, [submitStatus]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formElement = e.target as HTMLFormElement;
    const formData = new FormData(formElement);

    try {
      // Track download lead
      facebookPixelEvents.lead({
        content_name: `Download Request - ${downloadFile.FileName}`,
        content_category: 'download',
        content_type: 'form_submission'
      });

      if (!portalId || !formId) {
        console.error('Missing required HubSpot configuration:', { portalId, formId });
        return;
      }

      const response = await fetch(`https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fields: [
            {
              objectTypeId: "0-1",
              name: "firstname",
              value: formData.get('name')?.toString().split(' ')[0]
            },
            {
              objectTypeId: "0-1",
              name: "lastname",
              value: formData.get('name')?.toString().split(' ').slice(1).join(' ') || "."
            },
            {
              objectTypeId: "0-1",
              name: "email",
              value: formData.get('email')?.toString()
            },
            {
              objectTypeId: "0-1",
              name: "phone",
              value: formData.get('phone')?.toString() || ""
            },
            {
              objectTypeId: "0-1",
              name: "type_of_homeschooling",
              value: formData.get('type_of_homeschooling')?.toString() || ""
            },
            {
              objectTypeId: "0-1",
              name: "student_age",
              value: formData.get('student_age')?.toString() || ""
            }
          ],
          context: {
            pageUri: window.location.href,
            pageName: document.title
          },
          legalConsentOptions: {
            consent: {
              consentToProcess: true,
              text: "EmploSkills is committed to protecting and respecting your privacy, and we'll only use your personal information to administer your account and to provide the products and services you requested from us.",
              communications: [
                {
                  value: Boolean(formData.get('marketingConsent')),
                  subscriptionTypeId: 999,
                  text: "I agree to receive communications from EmploSkills"
                }
              ]
            }
          }
        })
      });

      if (!response.ok) {
        throw new Error('Submission failed');
      }

      facebookPixelEvents.completeRegistration({
        content_name: `Download Complete - ${downloadFile.FileName}`,
        content_category: 'download',
        content_type: 'registration_success'
      });

      handleSuccessfulSubmission();
    } catch (error) {
      console.error('Error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessfulSubmission = async () => {
    console.log('Download file info:', downloadFile);

    if (downloadFile?.FileUrl) {
        // Set form data back to initial state
        setFormData({
            name: '',
            email: '',
            phone: '',
            type_of_homeschooling: '',
            student_age: ''
        });
        setSubmitStatus('success');

        try {
            await fileService.downloadFile(downloadFile);
            console.log('File download initiated successfully');

            // Call onSuccess callback after a delay to show success message
            if (onSuccess) {
                setTimeout(onSuccess, 1500);
            }
        } catch (error) {
            console.error('Error downloading file:', error);
            setSubmitStatus('error');
        }
    } else {
        console.error('Missing required download properties:', {
            hasFileUrl: Boolean(downloadFile?.FileUrl)
        });
        setSubmitStatus('error');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {submitStatus === 'success' && (
        <div className="bg-green-50 p-4 rounded-md flex items-center text-green-700">
          <CheckCircle className="w-5 h-5 mr-2" />
          Successfully signed up! Your download should begin automatically.
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="bg-red-50 p-4 rounded-md flex items-center text-red-700">
          <AlertCircle className="w-5 h-5 mr-2" />
          Something went wrong. Please try again.
        </div>
      )}

      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700">
          Full Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
          disabled={isSubmitting}
          placeholder="John Doe"
        />
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700">
          Email <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
          disabled={isSubmitting}
          placeholder="john@example.com"
        />
      </div>

      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
          Phone Number <span className="text-gray-400 text-xs">(optional)</span>
        </label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={(e) => {
            const cleaned = e.target.value.replace(/[^\d\s()+\-]/g, '');
            setFormData({ ...formData, phone: cleaned });
          }}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          disabled={isSubmitting}
          placeholder="+1 (555) 555-5555"
          pattern="[\d\s()+\-]*"
          title="Please enter a valid phone number"
        />
      </div>

      <div>
        <label htmlFor="type_of_homeschooling" className="block text-sm font-medium text-gray-700">
          Type of Homeschooling <span className="text-red-500">*</span>
        </label>
        <select
          id="type_of_homeschooling"
          name="type_of_homeschooling"
          value={formData.type_of_homeschooling}
          onChange={(e) => setFormData({ ...formData, type_of_homeschooling: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
          disabled={isSubmitting}
        >
          <option value="">Select a school type</option>
          {schoolTypes.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="student_age" className="block text-sm font-medium text-gray-700">
          Student Age Range <span className="text-red-500">*</span>
        </label>
        <select
          id="student_age"
          name="student_age"
          value={formData.student_age}
          onChange={(e) => setFormData({ ...formData, student_age: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
          disabled={isSubmitting}
        >
          <option value="">Select an age range</option>
          {studentAgeRanges.map((range) => (
            <option key={range} value={range}>
              {range}
            </option>
          ))}
        </select>
      </div>

      <div className="text-xs text-gray-500">
        By signing up, you'll receive the lesson plan and occasional educational updates from us.
      </div>

      <div className="mt-6">
        <PrivacyConsent 
          onConsentChange={(field, value) => console.log(field, value)}
          required={true}
        />
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className={`w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-base font-medium text-white transition-all duration-200 ${
          isSubmitting 
            ? 'bg-blue-400 cursor-not-allowed' 
            : 'bg-primary hover:bg-blue-700'
        }`}
      >
        <Send className="w-5 h-5 mr-2" />
        {isSubmitting ? 'Processing...' : 'Download Lesson Plan'}
      </button>
    </form>
  );
}